import type { ReactNode } from "react";

import type { AppShellBreadcrumbItem } from "@/shared/components/layout/AppShellBreadcrumbContext";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { WhatsAppIcon } from "@/shared/components/notifications/WhatsAppIcon";

export interface ProductNotificationsLayoutProps {
  breadcrumb: AppShellBreadcrumbItem[];
  /** Mono uppercase tag above the title — names the product this page configures. */
  eyebrow: string;
  title: string;
  subtitle: string;
  heroTitle: string;
  heroSub: string;
  /** Right side of the hero: the active-types tally, where the product has one. */
  heroAside?: ReactNode;
  /** Under the hero: the phone numbers the page uses (where alerts go, where tests go). */
  controls?: ReactNode;
  /** Banner between the hero and the groups — the pending-availability notice, or an error. */
  notice?: ReactNode;
  children: ReactNode;
  footnote?: ReactNode;
}

/**
 * The common shell of the per-product WhatsApp notification pages.
 *
 * It exists so the pages cannot drift apart: this configuration used to be one section inside general
 * Settings, and the point of moving it into each product is that a user finds the same screen wherever they
 * look. One layout, one catalogue per product.
 */
export function ProductNotificationsLayout({
  breadcrumb,
  eyebrow,
  title,
  subtitle,
  heroTitle,
  heroSub,
  heroAside,
  controls,
  notice,
  children,
  footnote,
}: ProductNotificationsLayoutProps) {
  useAppShellBreadcrumb(breadcrumb);

  return (
    <div className="mx-auto w-full max-w-[940px] pb-12">
      <header className="mb-6">
        <div className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
          {eyebrow}
        </div>
        <h1 className="text-[26px] font-semibold tracking-tight text-foreground">{title}</h1>
        <div className="mt-1 max-w-[640px] text-sm text-muted-foreground">{subtitle}</div>
      </header>

      <div className="mb-5 rounded-xl border border-border bg-muted/30 p-[18px]">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex items-start gap-4 md:flex-1">
            <div
              className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white"
              style={{ background: "#25D366", boxShadow: "0 2px 8px rgba(37, 211, 102, 0.25)" }}
            >
              <WhatsAppIcon size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-foreground">{heroTitle}</div>
              <div className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{heroSub}</div>
            </div>
          </div>
          {heroAside}
        </div>
        {controls && <div className="mt-4 border-t border-border pt-4">{controls}</div>}
      </div>

      {notice && <div className="mb-5">{notice}</div>}

      <div className="flex flex-col gap-6">{children}</div>

      {footnote && <p className="mt-5 text-[11.5px] text-muted-foreground">{footnote}</p>}
    </div>
  );
}
